/**
 * ProceduralGates (v3) — shared helpers + individual gate builders used by the
 * per-entity-family rule files.
 *
 * Ported from SPFx `services/ProceduralGates.ts` (ProceduralGatesEngine) with
 * the same categories: requiredField, entityRestriction, authorization,
 * relationship, documentCollection, compliance.  Instead of a monolithic
 * `validateCart()`, v3 splits the rules per family (see `./rules/*Gates.ts`);
 * this module exposes the shared building blocks.
 *
 * NO REACT IMPORTS — pure TypeScript logic so it can run in Azure Function
 * contexts too.
 */

import {
  type CartSnapshot,
  type DocumentSnapshot,
  type GateCategory,
  type GateResult,
  type GateStatus,
  type PersonSnapshot,
  type ProductSnapshot,
  GATE_LABELS,
} from './workflow-engine/types';
import { noTinWaivesTaxId } from './rules/requiredIndividualFields';

// ---------------------------------------------------------------------------
// Soft-gate allowlist (ported verbatim from SPFx SOFT_GATE_OVERRIDES).
// Engines may look up these codes to decide whether a config flip to 'soft'
// is legal; values are the default taskDueDays for the follow-up.
// ---------------------------------------------------------------------------

export const SOFT_GATE_OVERRIDES: Record<string, number> = {
  DOC_MISSING_STATE_DOCS: 30,
  DOC_MISSING_BANKING_RES: 30,
  DOC_MISSING_PARTNER_AGREE: 30,
  DOC_MISSING_TRUST_1ST: 30,
  DOC_MISSING_CERT_TRUST: 30,
  DOC_MISSING_COURT_DOCS: 14,
  DOC_MISSING_FICT_NAME: 30,
  DOC_MISSING_BO_FORM: 30,
  DOC_MISSING_W9: 30,
  DOC_OPT_W9: 30,
  DOC_OPT_BO_FORM: 30,
  REPPAYEE_SSA_LETTER: 14,
  PUBFUNDS_FINANCE_AUTH: 7,
  AUTH_FINANCE_REQUIRED: 7,
  ESTATE_COURT_DOCS: 14,
  CONSERV_COURT_DOCS: 14,
};

// ---------------------------------------------------------------------------
// Helpers — person / cart access
// ---------------------------------------------------------------------------

export function getPrimary(cart: CartSnapshot): PersonSnapshot | undefined {
  return cart.people.find((p) => p.role === 'primary') ?? cart.people[0];
}

export function getByRole(cart: CartSnapshot, role: string): PersonSnapshot[] {
  return cart.people.filter((p) => p.role === role);
}

export function countOwners(cart: CartSnapshot): number {
  return cart.people.filter((p) => p.role === 'primary' || p.role === 'joint').length;
}

export function productHasOdp(product: ProductSnapshot): boolean {
  if (product.odpEnabled) return true;
  const f = product.featureSelections;
  return !!(f && (f['odp'] === true || f['odpEnabled'] === true));
}

export function productHasDebit(product: ProductSnapshot): boolean {
  const f = product.featureSelections;
  return !!(f && (f['debitCard'] === true || f['debit'] === true));
}

function nonEmpty(v: unknown): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}

export function getField(p: PersonSnapshot, key: keyof PersonSnapshot): string {
  const v = p[key];
  return typeof v === 'string' ? v.trim() : '';
}

// ---------------------------------------------------------------------------
// Gate builders — one helper per gate family.  Each returns either 0-or-1
// GateResult so rule files can `.push(...buildMissingField(...))`.
// ---------------------------------------------------------------------------

export interface FieldSpec {
  field: keyof PersonSnapshot;
  label: string;
}

/** Hard-fail required-field gate (category=requiredField, status=fail). */
export function buildRequiredFieldGates(
  cart: CartSnapshot,
  person: PersonSnapshot,
  specs: ReadonlyArray<FieldSpec>,
): GateResult[] {
  const gates: GateResult[] = [];
  // Existing-CIF person: Core Director already holds the full record and the
  // cart snapshot may only carry partial data for them. Server parity —
  // CartCommitService.ValidateAsync applies required-field checks only to
  // people WITHOUT a cifId (Bill 2026-07-23 tri-layer alignment).
  if (nonEmpty(person.cifId)) return gates;
  // No-TIN proof (Bill 2026-10-14): TIN code N/Forn + a clean name+DOB search waives the SSN.
  const waiveTaxId = noTinWaivesTaxId(person);
  for (const spec of specs) {
    if (spec.field === 'taxId' && waiveTaxId) continue;
    if (!nonEmpty(person[spec.field])) {
      const code = `MISSING_${String(spec.field).toUpperCase()}`;
      gates.push({
        gateCode: code,
        entityType: cart.entityType ?? '',
        category: 'requiredField',
        status: 'fail',
        label: GATE_LABELS[code] ?? `${spec.label} required`,
        reason: `${spec.label} is required for ${cart.entityType ?? 'this'} accounts.`,
        remediationAction: {
          label: `Add ${spec.label}`,
          deepLinkStepId: 'people',
          fieldId: String(spec.field),
        },
      });
    }
  }
  return gates;
}

/** CIP gate — primary ID must be captured and not expired. */
export function buildCipGate(cart: CartSnapshot): GateResult {
  const primary = getPrimary(cart);
  if (!primary) {
    return {
      gateCode: 'CIP',
      entityType: cart.entityType ?? '',
      category: 'compliance',
      status: 'blocked',
      label: GATE_LABELS.CIP!,
      reason: 'No primary applicant — CIP cannot be evaluated.',
      remediationAction: { label: 'Add primary applicant', deepLinkStepId: 'people' },
    };
  }
  const extra = (primary.extra ?? {}) as Record<string, unknown>;
  // CIP capture (IndividualCipScanSection) stores the ID type on extra.idType —
  // NOT idTypeCode. Read idType first (the real captured key); fall back to the
  // snapshot's idTypeCode / extra.idTypeCode for any legacy callers. (Fixes the
  // false "Primary ID not captured" gate that blocked commit despite a captured ID.)
  const idType = (typeof primary.idTypeCode === 'string' && primary.idTypeCode)
    || (typeof extra.idType === 'string' && extra.idType)
    || (typeof extra.idTypeCode === 'string' && extra.idTypeCode)
    || '';
  const idNum = primary.idNumber ?? (typeof extra.idNumber === 'string' ? extra.idNumber : '');
  const idIssuer = primary.idIssuer ?? (typeof extra.idIssuer === 'string' ? extra.idIssuer : '');
  const idExpiry = primary.idExpiryDate ?? (typeof extra.idExpiryDate === 'string' ? extra.idExpiryDate : '');

  if (!idType || !idNum || !idIssuer) {
    return {
      gateCode: 'CIP',
      entityType: cart.entityType ?? '',
      category: 'compliance',
      status: 'fail',
      label: GATE_LABELS.CIP!,
      reason: 'Primary ID not captured (type, number, issuer all required).',
      remediationAction: { label: 'Capture primary ID', deepLinkStepId: 'review' },
    };
  }
  if (isExpired(idExpiry)) {
    return {
      gateCode: 'CIP',
      entityType: cart.entityType ?? '',
      category: 'compliance',
      status: 'fail',
      label: GATE_LABELS.CIP!,
      reason: `Primary ID expired on ${idExpiry}. Capture a valid ID.`,
      remediationAction: { label: 'Recapture ID', deepLinkStepId: 'review' },
    };
  }
  return {
    gateCode: 'CIP',
    entityType: cart.entityType ?? '',
    category: 'compliance',
    status: 'pass',
    label: GATE_LABELS.CIP!,
  };
}

/** OFAC gate — status must be 'clear' on every person. */
export function buildOfacGate(cart: CartSnapshot): GateResult {
  const anyHit = cart.people.some((p) => p.ofacStatus === 'hit');
  if (anyHit) {
    return {
      gateCode: 'OFAC',
      entityType: cart.entityType ?? '',
      category: 'compliance',
      status: 'blocked',
      label: GATE_LABELS.OFAC!,
      reason: 'One or more applicants returned a potential OFAC match — escalate to Compliance.',
      remediationAction: { label: 'Review OFAC hit', deepLinkStepId: 'review' },
    };
  }
  // Existing-CIF persons are EXEMPT from the "must have a modal run" branch
  // (Bill 2026-07-23 tri-layer alignment): the modal deliberately does not
  // auto-run OFAC for existing customers (2026-05-21 rule), and the server
  // rescreen backstop screens them silently at commit-validate (alpha.354).
  // A recorded HIT on anyone — existing or new — still blocks above.
  const anyPending = cart.people.some(
    (p) => !p.cifId && (!p.ofacStatus || p.ofacStatus === 'idle' || p.ofacStatus === 'running'));
  if (anyPending) {
    return {
      gateCode: 'OFAC',
      entityType: cart.entityType ?? '',
      category: 'compliance',
      status: 'fail',
      label: GATE_LABELS.OFAC!,
      reason: 'OFAC screen has not been run for every new applicant.',
      remediationAction: { label: 'Run OFAC screen', deepLinkStepId: 'review' },
    };
  }
  const anyError = cart.people.some((p) => p.ofacStatus === 'error');
  if (anyError) {
    return {
      gateCode: 'OFAC',
      entityType: cart.entityType ?? '',
      category: 'compliance',
      status: 'warn',
      label: GATE_LABELS.OFAC!,
      reason: 'One or more OFAC screens errored — retry before committing.',
      remediationAction: { label: 'Retry OFAC', deepLinkStepId: 'review' },
    };
  }
  return {
    gateCode: 'OFAC',
    entityType: cart.entityType ?? '',
    category: 'compliance',
    status: 'pass',
    label: GATE_LABELS.OFAC!,
  };
}

/** Document collection gate — required docs collected. */
export function buildDocumentGates(cart: CartSnapshot): GateResult[] {
  const gates: GateResult[] = [];
  if (!cart.documents || cart.documents.length === 0) return gates;

  for (const doc of cart.documents) {
    if (!doc.required || doc.collected) continue;
    const code = `DOC_MISSING_${doc.docType.toUpperCase()}`;
    gates.push({
      gateCode: code,
      entityType: cart.entityType ?? '',
      category: 'documentCollection',
      status: 'fail',
      label: `${humanDoc(doc.docType)} collected`,
      reason: `Required document "${humanDoc(doc.docType)}" has not been collected.`,
      remediationAction: { label: 'Upload document', deepLinkStepId: 'review' },
    });
  }
  return gates;
}

// `buildComplianceQuestionsGate` retired 2026-05-08 with ComplianceQuestion
// kill plan. Compliance gating happens inline per-stage now (workflow gates +
// per-product rules), not via a separate checklist. Function removed.

/**
 * Relationship-count gate — rule file asserts a minimum count of people in
 * a given role (e.g. Trust requires 1+ trustee).
 */
export function buildRelationshipCountGate(
  cart: CartSnapshot,
  role: string,
  minCount: number,
  gateCode: string,
  label: string,
  remediationLabel = 'Add relationship',
): GateResult | null {
  const count = getByRole(cart, role).length;
  if (count >= minCount) return null;
  return {
    gateCode,
    entityType: cart.entityType ?? '',
    category: 'relationship',
    status: 'fail',
    label,
    reason: `${label} — need at least ${minCount}, currently ${count}.`,
    remediationAction: { label: remediationLabel, deepLinkStepId: 'people' },
  };
}

/** Product-feature-restriction gate — returns 0-N results. */
export function buildFeatureRestrictionGates(
  cart: CartSnapshot,
  restrictions: ReadonlyArray<{
    gateCode: string;
    label: string;
    predicate: (product: ProductSnapshot) => boolean;
    reason: string;
  }>,
): GateResult[] {
  const gates: GateResult[] = [];
  for (const rule of restrictions) {
    for (const product of cart.products) {
      if (rule.predicate(product)) {
        gates.push({
          gateCode: rule.gateCode,
          entityType: cart.entityType ?? '',
          category: 'entityRestriction',
          status: 'fail',
          label: rule.label,
          reason: `${rule.reason} (product ${product.code})`,
          remediationAction: { label: 'Adjust product', deepLinkStepId: 'products' },
        });
      }
    }
  }
  return gates;
}

/** Generic "pass" stub (keeps rule file output small). */
export function passGate(
  code: string,
  entityType: string,
  category: GateCategory = 'compliance',
  label?: string,
): GateResult {
  return {
    gateCode: code,
    entityType,
    category,
    status: 'pass',
    label: label ?? GATE_LABELS[code] ?? code,
  };
}

/**
 * Risk-review gate evaluator — translates cart.riskReviewStatus into the
 * canonical gate status for RISK_MGMT_REVIEW / COURT_ORDER_REVIEW.
 *
 * Reads CartSnapshot.riskReviewStatus (set server-side by RiskReviewFunction
 * when Risk Mgmt decides at pipeline step 7→8) and maps:
 *   - undefined / null → 'warn' (review not yet kicked off — pre-pipeline state)
 *   - 'NotRequired'    → 'pass' (admin override; bypassed)
 *   - 'Pending'        → 'warn' (submitted, awaiting decision)
 *   - 'Returned'       → 'warn' (RM sent back for FSR clarification)
 *   - 'Approved'       → 'pass' (RM cleared the cart to proceed)
 *   - 'Rejected'       → 'fail' (blocks commit — cart routes to Adverse Action)
 *
 * The remediation deep-link points at the 'review' step where the FSR can see
 * the decision detail (RM comment, decided-by/at) surfaced in ReviewStep.
 */
export function buildRiskReviewGate(
  cart: CartSnapshot,
  code: 'RISK_MGMT_REVIEW' | 'COURT_ORDER_REVIEW',
  label: string,
  category: GateCategory = 'authorization',
): GateResult {
  const entityType = cart.entityType ?? '';
  const status = cart.riskReviewStatus;

  if (status === 'Approved' || status === 'NotRequired') {
    return {
      gateCode: code,
      entityType,
      category,
      status: 'pass',
      label,
      reason: status === 'NotRequired'
        ? 'Risk Mgmt review not required (admin override).'
        : 'Risk Mgmt review approved.',
    };
  }

  if (status === 'Rejected') {
    return {
      gateCode: code,
      entityType,
      category,
      status: 'fail',
      label,
      reason: 'Risk Management rejected this cart. Cart routes to Adverse Action.',
      remediationAction: { label: 'View risk decision', deepLinkStepId: 'review' },
    };
  }

  // Pending / Returned / undefined → warn (review open or not yet started).
  return {
    gateCode: code,
    entityType,
    category,
    status: 'warn',
    label,
    reason: status === 'Pending'
      ? 'Risk Management review submitted, awaiting decision.'
      : status === 'Returned'
        ? 'Risk Management returned the cart for FSR clarification.'
        : 'Risk Management review will be submitted at pipeline step 7→8.',
    remediationAction: { label: 'View status', deepLinkStepId: 'review' },
  };
}

/** Generic "pending" gate — used by config-driven complianceGates with no evaluator. */
export function pendingGate(
  code: string,
  entityType: string,
  category: GateCategory = 'compliance',
  label?: string,
  reason?: string,
): GateResult {
  const base: GateResult = {
    gateCode: code,
    entityType,
    category,
    status: 'pending' as GateStatus,
    label: label ?? GATE_LABELS[code] ?? code,
  };
  if (reason) base.reason = reason;
  return base;
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function isExpired(iso: string | undefined): boolean {
  if (!iso) return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return false;
  const exp = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return exp.getTime() < Date.now();
}

function humanDoc(code: string): string {
  return code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}
